import React, { useContext } from 'react';
import c from 'classnames';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import { PopupContext } from '../../../../popups/PopupContext';
import ModalBody from '../../../base/ModalBody';
import ModalFormBody, { ModalFormBodyProps } from '../../../base/ModalFormBody';
import s from './ModalLayout.scss';

export interface Props {
  isFullscreen?: boolean;

  /**
   * Displayed in a row at the top of the modal. Consider using `<ModalHeader />`
   */
  header?: React.ReactNode;

  /**
   * Passing `true` will add a border to the bottom of the header
   */
  headerBorder?: boolean;

  /**
   * Displayed below the header and before the content. This tends to be where we put extra controls that don't fit
   * in the modal's content area.
   */
  preamble?: React.ReactNode;

  /**
   * Displayed in the middle of the modal. We don't have standard content spacing defined, so you're on your own with
   * regards to that.
   */
  content: React.ReactNode;

  /**
   * Displayed in a row at the bottom of the modal. Consider using `<ModalFooter />`
   */
  footer?: React.ReactNode;

  /**
   * Passing `true` will add a border to the top of the footer
   */
  footerBorder?: boolean;

  /**
   * A `className` that gets passed to the root element.
   */
  className?: string;

  /**
   * A `className` for the content of the modal.
   */
  contentClassName?: string;

  /**
   * Controls how much padding the header has.
   */
  headerPadding?: 'default' | 'slim' /* DS8.1.5 */;

  /**
   * Controls how much padding the footer has.
   *
   * `"default"`: 16px by 32px
   * `"none"`: 0px all around
   * `"horizontally-less"`: 16px by 24px
   *
   *
   * This replaced `footerClassName` to reduce the scope of futzing allowed in the footer.
   * Ideally, we wouldn't have this at all, but further planning and refactoring is required to
   * remove the usages of this prop.
   */
  footerPadding?: 'default' | 'none' | 'horizontally-less';

  /**
   * Ref of node with the overflow on it
   */
  innerScrollRef?: (node) => void;

  /** Wrap content in a scrollable div. Default is true */
  withScrollableContentWrapper?: boolean;

  /**
   * Will wrap the body contents in a form element and add this submit handler to it. Useful when the whole modal should
   * be treated as a form.
   */
  onSubmit?: ModalFormBodyProps['onSubmit'];
}

/**
 * A modal layout with slots for a `header`, `content`, and `footer`.
 *
 * Note: If you need the slots separated by borders, consider using `<SegmentedModalLayout />`. They're separated for now
 * for safety down the road. There are too many possible modal designs to put all of these style options into one component.
 */
const ModalLayout: React.FC<Props> = ({
  isFullscreen,
  header,
  headerBorder,
  preamble,
  content,
  footer,
  footerBorder,
  className,
  contentClassName,
  headerPadding = 'default',
  footerPadding = 'default',
  innerScrollRef,
  onSubmit,
  withScrollableContentWrapper = true,
}) => {
  const { setDefaultScrollableRef } = useContext(PopupContext);

  const children = (
    <>
      <div
        className={c(
          s['header-and-preamble'],
          headerBorder && s['header-and-preamble-border'],
          headerPadding === 'slim' && s['padding-slim']
        )}
      >
        <div className={s['header']}>{header}</div>
        {
          /** We expect the consumer to handle the layouting here */
          preamble
        }
      </div>

      <div
        className={c(
          s['content'],
          withScrollableContentWrapper && s['scrollable'],
          contentClassName
        )}
        ref={node => {
          if (withScrollableContentWrapper) {
            innerScrollRef && innerScrollRef(node);
            // Set the default scrollable, see ModalRoot for the logic
            setDefaultScrollableRef && setDefaultScrollableRef(node);
          }
        }}
      >
        {content}
      </div>
      {footer && (
        <div
          className={c(
            s['footer'],
            footerBorder && s['footer-border'],
            s[`footer-padding-${footerPadding}`]
          )}
        >
          {footer}
        </div>
      )}
    </>
  );

  return onSubmit ? (
    <ModalFormBody
      className={c(s['root'], isFullscreen && s['fullscreen'], className)}
      surfaceType={ThemeSurface.TERTIARY}
      isFullscreen={isFullscreen}
      onSubmit={onSubmit}
    >
      {children}
    </ModalFormBody>
  ) : (
    <ModalBody
      className={c(s['root'], isFullscreen && s['fullscreen'], className)}
      surfaceType={ThemeSurface.TERTIARY}
      isFullscreen={isFullscreen}
    >
      {children}
    </ModalBody>
  );
};

ModalLayout.displayName = 'ModalLayout';

export default ModalLayout;
